/**
 * ASCII Art Generator - DotCross 渲染引擎
 *
 * 使用点和叉符号根据亮度渲染
 * 支持动画渲染
 * Wave 8.3: 支持灰度预计算缓存
 */

import type { IRenderEngine, FullRenderSettings, RenderResult, StyleType, RGB } from '../types'
import type { AnimationContext } from '../animation/types'
import type { RenderContextExt } from './animationHelpers'
import { samplePixel, samplePixelFast, applyContrastAndBrightness, applyColorMode, invertBrightness } from '../algorithms/grayscale'
import {
  calculateCharacterAnimation,
  applyPulseToCharacterBrightness,
  createStaticContext,
  hasGrayscaleCache,
} from './animationHelpers'

// 点叉字符集: 空格, 点, 加号, 叉, 井号
const DOTCROSS_CHARSET = ' ·+×#'

export class DotCrossEngine implements IRenderEngine {
  readonly name: StyleType = 'dotcross'

  /**
   * 渲染图片为ASCII艺术（支持动画）
   * Wave 8.3: 支持灰度预计算缓存优化
   */
  render(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: FullRenderSettings,
    canvas: HTMLCanvasElement,
    context?: AnimationContext
  ): RenderResult {
    // 使用提供的动画上下文，或创建静态上下文
    const animContext: RenderContextExt = context ?? createStaticContext()

    const imageData = this.getImageData(source)
    const { width: imgWidth, height: imgHeight } = imageData

    const { fontSize, charSpacing, contrast, brightness, invert } = settings
    const charWidth = fontSize * 0.6 * charSpacing
    const charHeight = fontSize

    const cols = Math.max(1, Math.floor(imgWidth / charWidth))
    const rows = Math.max(1, Math.floor(imgHeight / charHeight))

    canvas.width = cols * charWidth
    canvas.height = rows * charHeight

    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = settings.backgroundColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    ctx.font = `${fontSize}px ${settings.renderFont}`
    ctx.textBaseline = 'top'

    const sampleWidth = imgWidth / cols
    const sampleHeight = imgHeight / rows

    // 检查动画或鼠标交互是否启用
    const animationEnabled = settings.ditherAnimationEnabled || settings.dither2AnimationEnabled
    const mouseInteractionEnabled = settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled
    const needsAnimation = animationEnabled || mouseInteractionEnabled

    // Wave 8.3: 检查是否有灰度缓存
    const useCache = hasGrayscaleCache(animContext)
    const grayscaleCache = animContext.grayscaleCache

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        // Wave 8.3: 智能采样
        const { brightness: rawBrightness, color } = useCache && grayscaleCache
          ? samplePixelFast(grayscaleCache, col * sampleWidth, row * sampleHeight, sampleWidth, sampleHeight)
          : samplePixel(imageData, col * sampleWidth, row * sampleHeight, sampleWidth, sampleHeight)

        let finalBrightness = useCache
          ? rawBrightness
          : applyContrastAndBrightness(rawBrightness, contrast, brightness * 255)

        if (invert) finalBrightness = invertBrightness(finalBrightness)

        // 计算字符基础位置
        const baseX = col * charWidth
        const baseY = row * charHeight

        // 应用动画或鼠标交互效果
        let drawX = baseX
        let drawY = baseY

        if (needsAnimation) {
          const animResult = calculateCharacterAnimation(
            baseX, baseY, animContext, settings, charWidth, charHeight
          )

          drawX += animResult.offsetX
          drawY += animResult.offsetY

          // 应用脉动效果到亮度（仅动画）
          if (animationEnabled) {
            finalBrightness = applyPulseToCharacterBrightness(finalBrightness, animResult.pulseFactor)
          }
        }

        const char = this.getDotCrossChar(finalBrightness)
        const finalColor = applyColorMode(color, settings.colorMode, settings.customColor)
        ctx.fillStyle = this.applyOpacity(finalColor, settings.opacity)
        ctx.fillText(char, drawX, drawY)
      }
    }

    return { canvas, width: canvas.width, height: canvas.height, charWidth, charHeight }
  }

  private getDotCrossChar(brightness: number): string {
    // 反转映射：高亮度 → 稀疏字符，低亮度 → 密集字符
    const index = Math.floor((1 - brightness / 255) * (DOTCROSS_CHARSET.length - 1))
    return DOTCROSS_CHARSET[Math.min(index, DOTCROSS_CHARSET.length - 1)]
  }

  getOutputSize(imageWidth: number, imageHeight: number, settings: FullRenderSettings): { width: number; height: number } {
    const { fontSize, charSpacing } = settings
    const charWidth = fontSize * 0.6 * charSpacing
    const charHeight = fontSize
    return { width: Math.floor(imageWidth / charWidth) * charWidth, height: Math.floor(imageHeight / charHeight) * charHeight }
  }

  private getImageData(source: HTMLImageElement | HTMLCanvasElement | ImageData): ImageData {
    if (source instanceof ImageData) return source
    const canvas = document.createElement('canvas')
    canvas.width = source.width
    canvas.height = source.height
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(source, 0, 0)
    return ctx.getImageData(0, 0, canvas.width, canvas.height)
  }

  private applyOpacity(color: RGB, opacity: number): string {
    return opacity < 1 ? `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})` : `rgb(${color.r}, ${color.g}, ${color.b})`
  }
}

export const dotcrossEngine = new DotCrossEngine()